import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Rocket,
  ChevronDown,
  ChevronRight,
  Building2,
  Users,
  CalendarCheck,
  IndianRupee,
  ShieldCheck,
  CalendarDays,
  Settings,
  CheckCircle2,
  AlertTriangle,
  Info,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";

/* ------------------------------------------------------------------ */
/*  Data — sourced from actual project files                          */
/* ------------------------------------------------------------------ */

const SETUP_STEPS = [
  {
    num: 1,
    label: "Organisation Profile",
    desc: "Set up your organisation name, legal name, PAN, TAN, state, and address. This is mandatory — BrixiGo blocks access until this is completed or skipped.",
    path: "/onboarding/org-profile",
    pathLabel: "Organisation Profile Wizard",
    Icon: Building2,
    tint: "ic-blue",
    required: true,
  },
  {
    num: 2,
    label: "Organisation Setup",
    desc: "Complete the 11-step setup wizard: organisation details, registration (GSTIN, PAN, TAN, CIN, EPFO, ESIC), departments, designations, locations, org settings, attendance method, leave configuration, payroll basics, employee numbering, and notifications.",
    path: "/Setup",
    pathLabel: "Setup Wizard",
    Icon: Settings,
    tint: "ic-slate",
    required: true,
  },
  {
    num: 3,
    label: "Add Employees",
    desc: "Add employees individually using the 5-step wizard (Basic Details, Organisation, Compensation, Banking, Statutory) or import in bulk via CSV/Excel. At least one employee is required before payroll can run.",
    path: "/employees?module=employees&view=add",
    pathLabel: "Add Employee",
    Icon: Users,
    tint: "ic-violet",
    required: true,
  },
  {
    num: 4,
    label: "Payroll & Salary Setup",
    desc: "Use the Guided Setup Wizard to configure payroll settings, salary components (earnings & deductions), calculation formulas, salary structure templates, and assign them to employees.",
    path: "/salary-v2/setup",
    pathLabel: "Payroll Setup Wizard",
    Icon: IndianRupee,
    tint: "ic-amber",
    required: true,
  },
  {
    num: 5,
    label: "Attendance Configuration",
    desc: "Choose an attendance method (Default Present, Manual, Excel Upload, or Biometric), set work schedule, configure weekly offs, and set up the holiday calendar.",
    path: "/settings/attendance-leave-setup",
    pathLabel: "Attendance Settings",
    Icon: CalendarCheck,
    tint: "ic-green",
    required: true,
  },
  {
    num: 6,
    label: "Statutory Setup",
    desc: "Configure PF, ESI, Professional Tax, Labour Welfare Fund, and TDS settings for compliance. This step is optional but recommended before processing payroll.",
    path: "/settings",
    pathLabel: "Settings > Statutory",
    Icon: ShieldCheck,
    tint: "ic-indigo",
    required: false,
  },
  {
    num: 7,
    label: "Leave Configuration",
    desc: "Set up leave types (CL, SL, EL, etc.), leave policies, entitlements, and approval workflows. This step is optional and can be configured later.",
    path: "/settings/leave-setup",
    pathLabel: "Leave Settings",
    Icon: CalendarDays,
    tint: "ic-teal",
    required: false,
  },
];

const AFTER_SETUP = [
  { label: "Process Payroll", desc: "Run monthly payroll for all or selected employees.", Icon: IndianRupee, tint: "ic-amber" },
  { label: "Manage Attendance", desc: "Mark daily attendance, import from spreadsheets, or use Apply Calendar.", Icon: CalendarCheck, tint: "ic-green" },
  { label: "Manage Leave", desc: "Employees can apply for leave; managers can approve or reject.", Icon: CalendarDays, tint: "ic-teal" },
  { label: "Generate Payslips", desc: "Generate and download payslips after finalising payroll.", Icon: IndianRupee, tint: "ic-rose" },
  { label: "View Reports", desc: "Access employee, attendance, payroll, and statutory reports.", Icon: Settings, tint: "ic-sky" },
  { label: "Manage Compliance", desc: "Handle PF, ESI, TDS filings and statutory reports.", Icon: ShieldCheck, tint: "ic-indigo" },
];

const FAQ_DATA = [
  {
    q: "What should I configure first in BrixiGo?",
    a: "Start with the Organisation Profile — BrixiGo requires your organisation name, state, and address before you can access any other module. After that, complete the Organisation Setup wizard at Setup to add departments, designations, and locations.",
  },
  {
    q: "Can I skip any setup steps?",
    a: "The Organisation Profile wizard can be skipped, but payroll and compliance features will remain limited. The Organisation Setup wizard steps for departments, designations, and locations are required — you need at least one of each. Statutory and Leave configuration are optional and can be done later.",
  },
  {
    q: "What is the minimum required before I can process payroll?",
    a: "BrixiGo requires 5 items to be complete before payroll can run: Organisation Profile, at least one Employee added, Payroll Settings configured (with an explicit save), Attendance Configuration set, and Salary Components configured. The Payroll Readiness Gate will show you which items are incomplete.",
  },
  {
    q: "Where do I configure my organisation details?",
    a: "Go to Settings > Organisation Profile > Company Profile to update your legal name, PAN, TAN, GSTIN, address, and state registration. For the initial setup, use the Organisation Profile Wizard at onboarding.",
  },
  {
    q: "How do I add departments, designations, and locations?",
    a: "Use the Setup wizard (accessible from the sidebar or at /Setup) — Steps 3, 4, and 5 let you add departments, designations, and locations. You can also manage them later from Settings > Company Structure.",
  },
  {
    q: "How do I set up salary components?",
    a: "Go to the Payroll Setup Wizard at Salary Engine > Guided Setup. Step 2 lets you configure salary components (Basic, HRA, Conveyance, Medical, Special Allowance, etc.). Standard components are auto-seeded on first visit.",
  },
  {
    q: "How do I assign a salary structure to an employee?",
    a: "After creating an employee, click 'Assign salary package' on the success screen. This takes you to the Employee Salary Structure page where you select a salary template and the employee's CTC is used as the base.",
  },
  {
    q: "Where can I see my setup progress?",
    a: "The Dashboard shows a Setup Progress Card with a completion percentage and a checklist of all 7 setup items. Each incomplete item links directly to its configuration page.",
  },
  {
    q: "What is the Setup wizard vs the Settings pages?",
    a: "The Setup wizard (/Setup) is an 11-step guided flow for initial configuration. The Settings pages provide more detailed, granular controls for each area. After completing the wizard, use Settings to fine-tune your configuration.",
  },
  {
    q: "Can employees use BrixiGo before setup is complete?",
    a: "Employees with the 'Employee' role can access self-service features (My Attendance, payslips, leave applications) even if admin setup is incomplete. Setup gates only apply to admin and HR users.",
  },
];

const ERROR_SOLUTIONS = [
  {
    error: "Your account is not linked to an organisation",
    cause: "Your user login is not associated with any BrixiGo organisation.",
    fix: "Contact your administrator to link your account, or if you are the admin, complete the registration and organisation creation process.",
  },
  {
    error: "Organisation Profile is incomplete — add name, PAN, TAN, state, and address",
    cause: "The Organisation Profile wizard was skipped or partially completed.",
    fix: "Go to Settings > Organisation Profile > Company Profile and fill in the missing fields. The dashboard also shows an 'Org Profile Banner' with a direct link.",
  },
  {
    error: "Initial setup is not complete. Add departments, designations, and locations",
    cause: "The Organisation Setup wizard was not completed — at least one department, designation, and location is required.",
    fix: "Go to Setup from the sidebar and complete Steps 3 (Departments), 4 (Designations), and 5 (Locations). You need at least one of each.",
  },
  {
    error: "No employees have been added yet",
    cause: "Payroll requires at least one employee to process.",
    fix: "Go to Employees > Add Employee and create at least one employee record using the 5-step wizard, or use Bulk Import to upload multiple employees.",
  },
  {
    error: "Payroll settings are incomplete. Payroll processing will not be available.",
    cause: "Payroll settings have not been explicitly saved. Auto-seeded defaults do not count.",
    fix: "Go to the Payroll Setup Wizard and complete Step 1 (Payroll Settings). You must click Save — visiting the page alone is not enough.",
  },
  {
    error: "Salary components are not configured",
    cause: "No salary components have been explicitly saved.",
    fix: "Go to the Payroll Setup Wizard > Step 2 (Components) and save your salary components. Standard components are auto-seeded on first visit, but you must save them.",
  },
  {
    error: "Attendance method is not configured",
    cause: "No attendance method has been selected in the setup.",
    fix: "Go to Settings > Attendance > Attendance Method and select one of: Default Present, Manual, Excel Upload, or Biometric.",
  },
  {
    error: "Organisation name is required (Setup Wizard)",
    cause: "The Organisation Name field was left blank in Step 1 of the Setup wizard.",
    fix: "Enter your organisation's name in the Organisation Name field. This is the only required field in Step 1.",
  },
  {
    error: "At least one department / designation / location is required",
    cause: "Steps 3, 4, or 5 of the Setup wizard have no entries.",
    fix: "Add at least one department, one designation, and one location. Type the name and press Enter or click Add.",
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function GettingStartedHelpPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const faqRef = useRef(null);

  const lc = (s) => s.toLowerCase();
  const match = (s) => lc(s).includes(lc(search));

  const filteredFaqs = search.trim()
    ? FAQ_DATA.filter((f) => match(f.q) || match(f.a))
    : FAQ_DATA;

  const filteredErrors = search.trim()
    ? ERROR_SOLUTIONS.filter((e) => match(e.error) || match(e.cause) || match(e.fix))
    : ERROR_SOLUTIONS;

  return (
    <div className="brix-page">
      {/* Hero */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-blue">
              <Rocket size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Getting Started</h1>
              <p className="hs-hero-sub">
                Set up and start using BrixiGo Payroll — step by step.
              </p>
            </div>
          </div>

          <form className="hs-search-wrap" onSubmit={(e) => { e.preventDefault(); if (search.trim() && faqRef.current) faqRef.current.scrollIntoView({ behavior: "smooth" }); }}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search — e.g. setup, company profile, PAN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      {/* ── Welcome ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Welcome to BrixiGo</h2>
        </div>
        <div className="ah-content-card">
          <p>
            BrixiGo is an HR & Payroll platform for Indian businesses. It handles employee
            management, attendance tracking, leave management, salary configuration, payroll
            processing, payslip generation, and statutory compliance (PF, ESI, TDS, Professional
            Tax, LWF).
          </p>
          <p>
            This guide walks you through the actual setup steps required to get your
            organisation up and running on BrixiGo.
          </p>
        </div>
      </section>

      {/* ── Before You Begin ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <AlertTriangle size={20} className="ah-section-icon ic-amber" />
          <h2 className="hs-section-title">Before You Begin</h2>
        </div>
        <div className="ah-content-card">
          <p>Make sure you have the following ready before starting setup:</p>
          <div className="ah-feature-grid">
            <div className="ah-feature-item">
              <Building2 size={18} className="ic-blue" />
              <span>Organisation name, legal name, and address</span>
            </div>
            <div className="ah-feature-item">
              <ShieldCheck size={18} className="ic-amber" />
              <span>PAN, TAN, GSTIN, EPFO code, ESIC code (if applicable)</span>
            </div>
            <div className="ah-feature-item">
              <Users size={18} className="ic-violet" />
              <span>Employee details (or a CSV/Excel file for bulk import)</span>
            </div>
            <div className="ah-feature-item">
              <IndianRupee size={18} className="ic-green" />
              <span>Salary structure and CTC information</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Setup Steps ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Rocket size={20} className="ah-section-icon ic-green" />
          <h2 className="hs-section-title">Getting Started Steps</h2>
        </div>
        <div className="ah-content-card">
          <p>
            BrixiGo tracks 7 setup items on your Dashboard. The first 5 are
            <strong> mandatory</strong> before you can process payroll. Items 6 and 7 are
            optional but recommended.
          </p>

          <div className="gs-steps-list">
            {SETUP_STEPS.map((step) => {
              const Icon = step.Icon;
              return (
                <div key={step.num} className="gs-step-card">
                  <div className="gs-step-num-col">
                    <div className={`gs-step-num ${step.required ? "gs-required" : "gs-optional"}`}>
                      {String(step.num).padStart(2, "0")}
                    </div>
                    {step.num < SETUP_STEPS.length && <div className="gs-step-line" />}
                  </div>
                  <div className="gs-step-body">
                    <div className="gs-step-title-row">
                      <div className={`gs-step-icon ${step.tint}`}>
                        <Icon size={18} strokeWidth={1.8} />
                      </div>
                      <div className="gs-step-title">{step.label}</div>
                      <span className={`gs-step-badge ${step.required ? "gs-badge-req" : "gs-badge-opt"}`}>
                        {step.required ? "Required" : "Optional"}
                      </span>
                    </div>
                    <div className="gs-step-desc">{step.desc}</div>
                    <button
                      type="button"
                      className="gs-step-link"
                      onClick={() => navigate(step.path)}
                    >
                      Go to {step.pathLabel} <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Payroll Readiness ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <CheckCircle2 size={20} className="ah-section-icon ic-green" />
          <h2 className="hs-section-title">Payroll Readiness</h2>
        </div>
        <div className="ah-content-card">
          <p>
            BrixiGo will not allow payroll processing until all 5 mandatory items are complete.
            When you try to process payroll, the <strong>Payroll Readiness Gate</strong> checks
            these items and shows which ones need attention:
          </p>
          <div className="ah-payroll-info">
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Organisation Profile</strong> — name, state, and address completed</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Employees Added</strong> — at least one employee created</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Payroll Settings</strong> — pay cycle and processing day saved (not just auto-seeded)</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Attendance Configuration</strong> — attendance method selected</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Salary Components</strong> — components configured and saved</span>
            </div>
            <div className="ah-payroll-item ah-payroll-note">
              <Info size={16} />
              <span>Your Dashboard shows a <strong>Setup Progress Card</strong> with completion percentage and direct links to each item</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── After Setup ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <CheckCircle2 size={20} className="ah-section-icon ic-teal" />
          <h2 className="hs-section-title">After Setup</h2>
        </div>
        <div className="ah-content-card">
          <p>Once setup is complete, you can use these BrixiGo features:</p>
          <div className="ah-feature-grid">
            {AFTER_SETUP.map((item) => {
              const Icon = item.Icon;
              return (
                <div key={item.label} className="ah-feature-item">
                  <Icon size={18} className={item.tint} />
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontWeight: 500, color: "var(--brix-slate-500, #64748b)", fontSize: "0.76rem" }}>{item.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FAQs ── */}
      <section className="ah-section" ref={faqRef}>
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="ah-content-card">
          <div className="hs-faq-list">
            {filteredFaqs.length === 0 && (
              <p className="ah-empty">No matching questions found.</p>
            )}
            {filteredFaqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={faq.q} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{faq.q}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">{faq.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Common Errors ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <AlertTriangle size={20} className="ah-section-icon ic-rose" />
          <h2 className="hs-section-title">Common Setup Issues & Solutions</h2>
        </div>
        <div className="ah-content-card">
          {filteredErrors.length === 0 && (
            <p className="ah-empty">No matching errors found.</p>
          )}
          <div className="ah-errors-list">
            {filteredErrors.map((item) => (
              <div key={item.error} className="ah-error-item">
                <div className="ah-error-title">
                  <AlertTriangle size={14} />
                  <code>{item.error}</code>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Why:</span>
                  <span>{item.cause}</span>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Fix:</span>
                  <span>{item.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
